// @ts-check
import { Component } from '@theme/component';

class LocalizationDialog extends Component {
  requiredRefs = [
    'dialog',
    'combobox',
    'countryInput',
    'countryTrigger',
    'countryTriggerText',
    'countryPopover',
    'countrySearch',
    'countryList',
    'languageSelect',
    'submitButton',
    'data',
    'form',
    'emptyState',
  ];

  /** @type {{ currentCountry?: string; currentLanguage?: string; activeIndex: number }} */
  state = {
    currentCountry: undefined,
    currentLanguage: undefined,
    activeIndex: -1,
  };

  /** @type {{ currentCountry?: string; currentLanguage?: string; countries?: any[]; languages?: any[] }} */
  data = { countries: [], languages: [] };

  /** @type {HTMLElement[]} */
  visibleOptions = [];

  /** @type {string | undefined} */
  blockId;

  /** @type {(event: Event) => void} */
  _blockSelectHandler = this.#handleBlockSelect.bind(this);

  /** @type {(event: Event) => void} */
  _blockDeselectHandler = this.#handleBlockDeselect.bind(this);

  /** @type {(event: MouseEvent) => void} */
  _dialogClickHandler = this.#handleDialogClick.bind(this);

  connectedCallback() {
    super.connectedCallback();
    this.#parseDataset();
    this.#bindDialogEvents();
    this.#bindFocusManagement();
    this.#bindThemeEditorEvents();
    this.#syncInputWithCountry();
    this.#updateVisibleOptions();
    this.#setComboboxExpanded(false);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const dialog = this.dialogElement;
    if (dialog) {
      dialog.removeEventListener('click', this._dialogClickHandler);
    }
    if (typeof Shopify !== 'undefined' && Shopify?.designMode) {
      document.removeEventListener('shopify:block:select', this._blockSelectHandler);
      document.removeEventListener('shopify:block:deselect', this._blockDeselectHandler);
    }
  }

  /**
   * @param {Event} event
   */
  open(event) {
    const trigger = event?.target ?? event?.currentTarget;
    this.activeTrigger = trigger instanceof HTMLElement ? trigger : undefined;
    this.#setTriggerExpanded(true);

    const dialog = this.dialogElement;
    if (!dialog.open) {
      dialog.showModal();
    }
  }

  close() {
    const dialog = this.dialogElement;
    if (dialog.open) {
      dialog.close();
    }
  }

  /**
   * @param {{country?: string, value?: string}} [data]
   */
  handleCountrySelect(data = {}) {
    const isoCode = typeof data?.country === 'string' ? data.country : data?.value;
    if (!isoCode) return;

    this.#setCountry(isoCode);
    this.#setComboboxExpanded(false);
  }

  handleCountryInput() {
    const query = this.countrySearchInput.value.trim().toLowerCase();
    this.#filterCountryOptions(query);
  }

  /**
   * @param {KeyboardEvent} event
   */
  handleCountryKeydown(event) {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.#moveActiveOption(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.#moveActiveOption(-1);
        break;
      case 'Enter':
        event.preventDefault();
        this.#commitActiveOption();
        break;
      case 'Escape':
        event.preventDefault();
        this.#setComboboxExpanded(false);
        break;
      default:
        break;
    }
  }

  handleComboboxToggle() {
    const isExpanded = this.comboboxElement.getAttribute('aria-expanded') === 'true';
    this.#setComboboxExpanded(!isExpanded);
  }

  /**
   * @param {Event} event
   */
  handleLanguageChange(event) {
    const target = event?.target;
    if (!(target instanceof HTMLSelectElement)) return;
    this.state.currentLanguage = target.value;
  }

  handleSubmit() {
    this.formElement.requestSubmit();
  }

  #parseDataset() {
    const raw = this.dataElement.textContent?.trim() ?? '';
    this.data = raw ? this.#safeParse(raw) : { countries: [], languages: [] };
    const currentCountry = this.data.currentCountry ?? this.data.countries?.[0]?.isoCode;
    const currentLanguage =
      this.data.currentLanguage ??
      this.#getCountryLanguages(currentCountry)?.[0]?.isoCode ??
      this.data.languages?.[0]?.isoCode;

    this.state = {
      currentCountry,
      currentLanguage,
      activeIndex: -1,
    };

    this.#populateLanguages(this.#getCountryLanguages(currentCountry));
    this.#highlightSelectedCountry();
  }

  /**
   * @param {string} raw
   */
  #safeParse(raw) {
    try {
      return JSON.parse(raw);
    } catch (error) {
      console.warn('Failed to parse localization dialog data', error);
      return { countries: [], languages: [] };
    }
  }

  #bindDialogEvents() {
    const dialog = this.dialogElement;
    dialog.addEventListener('close', () => {
      this.#setTriggerExpanded(false);
      this.#setComboboxExpanded(false);
      if (this.activeTrigger instanceof HTMLElement) {
        this.activeTrigger.setAttribute('aria-expanded', 'false');
        this.activeTrigger.focus();
      }
      this.activeTrigger = undefined;
    });

    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.close();
    });

    dialog.addEventListener('click', this._dialogClickHandler);

    this.languageSelectElement.addEventListener('change', (event) => this.handleLanguageChange(event));
  }

  /**
   * @param {MouseEvent} event
   */
  #handleDialogClick(event) {
    const dialog = this.dialogElement;
    // If the click is directly on the dialog (backdrop), close it
    if (event.target === dialog) {
      this.close();
    }
  }

  #bindFocusManagement() {
    this.addEventListener('focusout', (event) => {
      if (!this.dialogElement.open) return;

      const relatedTarget = event.relatedTarget;
      if (!(relatedTarget instanceof Node) || this.dialogElement.contains(relatedTarget)) {
        return;
      }

      if (!this.contains(relatedTarget)) {
        this.#setComboboxExpanded(false);
      }
    });

    // Close popover when clicking outside
    document.addEventListener('click', (event) => {
      if (!this.dialogElement.open || this.countryPopoverElement.hidden) return;

      const target = event.target;
      if (!(target instanceof Node)) return;

      if (!this.comboboxElement.contains(target)) {
        this.#setComboboxExpanded(false);
      }
    });
  }

  #bindThemeEditorEvents() {
    // Get block ID from data attribute
    this.blockId = this.dataset.blockId;

    // Only bind theme editor events if in design mode
    if (typeof Shopify !== 'undefined' && Shopify?.designMode) {
      document.addEventListener('shopify:block:select', this._blockSelectHandler);
      document.addEventListener('shopify:block:deselect', this._blockDeselectHandler);
    }
  }

  /**
   * @param {Event} event
   */
  #handleBlockSelect(event) {
    if (!this.dialogElement) return;

    // Check if the event concerns this block
    const customEvent = /** @type {CustomEvent} */ (event);
    const eventBlockId = customEvent.detail?.blockId;

    if (eventBlockId && this.blockId && eventBlockId === this.blockId) {
      // Open the dialog in the editor when this block is selected
      if (!this.dialogElement.open) {
        this.dialogElement.showModal();
      }
    } else {
      // Close the dialog when another block is selected
      if (this.dialogElement.open) {
        this.dialogElement.close();
      }
    }
  }

  /**
   * @param {Event} event
   */
  #handleBlockDeselect(event) {
    if (!this.dialogElement) return;

    // Check if the event concerns this block
    const customEvent = /** @type {CustomEvent} */ (event);
    const eventBlockId = customEvent.detail?.blockId;
    if (eventBlockId && this.blockId && eventBlockId === this.blockId) {
      // Close the dialog when this block is deselected
      if (this.dialogElement.open) {
        this.dialogElement.close();
      }
    }
  }

  #syncInputWithCountry() {
    this.countryHiddenInput.value = this.state.currentCountry ?? '';
    const displayValue = this.#getCountryDisplayValue(this.state.currentCountry);
    this.countryTriggerTextElement.textContent = displayValue;
    this.countrySearchInput.value = '';
  }

  /**
   * @param {string | undefined} isoCode
   */
  #setCountry(isoCode) {
    const country = this.#findCountry(isoCode);
    if (!country) return;

    this.state.currentCountry = country.isoCode;
    this.countryHiddenInput.value = country.isoCode;
    const displayValue = this.#getCountryDisplayValue(country.isoCode);
    this.countryTriggerTextElement.textContent = displayValue;
    this.countrySearchInput.value = '';
    this.#highlightSelectedCountry();
    this.#populateLanguages(this.#getCountryLanguages(country.isoCode));
    this.#updateVisibleOptions();
  }

  /**
   * @param {Array<{isoCode: string, endonymName?: string, name?: string}>} [countryLanguages=[]]
   */
  #populateLanguages(countryLanguages = []) {
    const languages =
      Array.isArray(countryLanguages) && countryLanguages.length > 0
        ? countryLanguages
        : this.data.languages ?? [];

    const fragment = document.createDocumentFragment();
    languages.forEach(
      /**
       * @param {{isoCode: string, endonymName?: string, name?: string}} language
       */
      (language) => {
        if (!language?.isoCode) return;
        const option = document.createElement('option');
        option.value = language.isoCode;
        option.textContent = this.#capitalizeFirstLetter(
          language.endonymName || language.name || language.isoCode
        );
        if (language.isoCode === this.state.currentLanguage) {
          option.selected = true;
        }
        fragment.appendChild(option);
      }
    );

    const languageSelect = this.languageSelectElement;
    languageSelect.replaceChildren(fragment);
    if (!languageSelect.value && languages.length > 0) {
      languageSelect.value = languages[0].isoCode;
    }

    this.state.currentLanguage = languageSelect.value;
  }

  /**
   * @param {string} value
   */
  #capitalizeFirstLetter(value) {
    if (typeof value !== 'string' || value.length === 0) return '';
    return value.charAt(0).toLocaleUpperCase() + value.slice(1);
  }

  /**
   * @param {string} query
   */
  #filterCountryOptions(query) {
    const options = this.#getCountryOptions();
    let visibleCount = 0;

    options.forEach((option) => {
      const text = option.textContent?.toLowerCase() ?? '';
      const match = text.includes(query);
      option.hidden = !match;
      option.closest('li')?.toggleAttribute('hidden', !match);
      if (match) visibleCount += 1;
    });

    this.#toggleGroupLabels();
    this.emptyStateElement.hidden = visibleCount > 0;
    this.#updateVisibleOptions();
  }

  #toggleGroupLabels() {
    const labels = this.countryListElement.querySelectorAll('.localization-dialog__group-label');
    labels.forEach((label) => {
      if (!(label instanceof HTMLElement)) return;
      let hasVisibleSibling = false;
      let sibling = label.nextElementSibling;

      while (sibling && !sibling.classList.contains('localization-dialog__group-label')) {
        if (!sibling.hasAttribute('hidden')) {
          hasVisibleSibling = true;
          break;
        }
        sibling = sibling.nextElementSibling;
      }

      label.toggleAttribute('hidden', !hasVisibleSibling);
    });
  }

  #updateVisibleOptions() {
    const options = this.#getCountryOptions().filter(
      (option) => !option.hidden && !option.closest('li')?.hasAttribute('hidden')
    );

    this.visibleOptions = options;
    const selectedIndex = options.findIndex((option) => option.dataset.value === this.state.currentCountry);
    this.state.activeIndex = selectedIndex >= 0 ? selectedIndex : 0;
    this.#updateActivedescendant();
  }

  /**
   * @param {number} delta
   */
  #moveActiveOption(delta) {
    if (!this.visibleOptions || this.visibleOptions.length === 0) return;
    const nextIndex = this.state.activeIndex + delta;
    if (nextIndex < 0 || nextIndex >= this.visibleOptions.length) return;

    this.state.activeIndex = nextIndex;
    this.#updateActivedescendant();

    const option = this.visibleOptions[this.state.activeIndex];
    option?.scrollIntoView({ block: 'nearest' });
  }

  #commitActiveOption() {
    const option = this.visibleOptions?.[this.state.activeIndex];
    if (!option) return;
    const value = option.dataset.value;
    if (value) {
      this.#setCountry(value);
    }
    this.#setComboboxExpanded(false);
  }

  #updateActivedescendant() {
    const option = this.visibleOptions?.[this.state.activeIndex];
    this.#getCountryOptions().forEach((element) => element.classList.remove('is-focused'));

    if (option?.id) {
      this.countrySearchInput.setAttribute('aria-activedescendant', option.id);
      option.classList.add('is-focused');
    } else {
      this.countrySearchInput.removeAttribute('aria-activedescendant');
    }
  }

  #highlightSelectedCountry() {
    const options = this.#getCountryOptions();
    options.forEach((option) => {
      const match = option.dataset.value === this.state.currentCountry;
      option.classList.toggle('is-selected', match);
      option.setAttribute('aria-selected', String(match));
    });
  }

  /**
   * @returns {HTMLElement[]}
   */
  #getCountryOptions() {
    const refs = this.refs.countryOptions;
    if (Array.isArray(refs)) return /** @type {HTMLElement[]} */ (refs);
    if (refs) return [/** @type {HTMLElement} */ (refs)];
    return [];
  }

  /**
   * @param {string | undefined} isoCode
   */
  #getCountryDisplayValue(isoCode) {
    const country = this.#findCountry(isoCode);
    if (!country) return '';
    return country.name;
  }

  /**
   * @param {string | undefined} isoCode
   */
  #findCountry(isoCode) {
    if (!isoCode) return undefined;
    return this.data.countries?.find((country) => country.isoCode === isoCode);
  }

  /**
   * @param {string | undefined} isoCode
   */
  #getCountryLanguages(isoCode) {
    const country = this.#findCountry(isoCode);
    return country?.languages ?? [];
  }

  /**
   * @param {boolean} isExpanded
   */
  #setComboboxExpanded(isExpanded) {
    const expanded = Boolean(isExpanded);
    this.comboboxElement.setAttribute('aria-expanded', String(expanded));
    this.countryTriggerElement.setAttribute('aria-expanded', String(expanded));
    this.countryPopoverElement.hidden = !expanded;

    if (expanded) {
      requestAnimationFrame(() => {
        this.countrySearchInput.focus();
      });
    } else {
      this.countrySearchInput.removeAttribute('aria-activedescendant');
      this.countrySearchInput.value = '';
      this.#filterCountryOptions('');
    }
  }

  /**
   * @param {boolean} isExpanded
   */
  #setTriggerExpanded(isExpanded) {
    if (this.activeTrigger instanceof HTMLElement) {
      this.activeTrigger.setAttribute('aria-expanded', String(Boolean(isExpanded)));
    }
  }

  /** @returns {HTMLDialogElement} */
  get dialogElement() {
    return /** @type {HTMLDialogElement} */ (this.#requireRef('dialog'));
  }

  /** @returns {HTMLButtonElement} */
  get countryTriggerElement() {
    return /** @type {HTMLButtonElement} */ (this.#requireRef('countryTrigger'));
  }

  /** @returns {HTMLElement} */
  get countryTriggerTextElement() {
    return /** @type {HTMLElement} */ (this.#requireRef('countryTriggerText'));
  }

  /** @returns {HTMLElement} */
  get countryPopoverElement() {
    return /** @type {HTMLElement} */ (this.#requireRef('countryPopover'));
  }

  /** @returns {HTMLInputElement} */
  get countrySearchInput() {
    return /** @type {HTMLInputElement} */ (this.#requireRef('countrySearch'));
  }

  /** @returns {HTMLInputElement} */
  get countryHiddenInput() {
    return /** @type {HTMLInputElement} */ (this.#requireRef('countryInput'));
  }

  /** @returns {HTMLSelectElement} */
  get languageSelectElement() {
    return /** @type {HTMLSelectElement} */ (this.#requireRef('languageSelect'));
  }

  /** @returns {HTMLElement} */
  get comboboxElement() {
    return /** @type {HTMLElement} */ (this.#requireRef('combobox'));
  }

  /** @returns {HTMLElement} */
  get emptyStateElement() {
    return /** @type {HTMLElement} */ (this.#requireRef('emptyState'));
  }

  /** @returns {HTMLFormElement} */
  get formElement() {
    return /** @type {HTMLFormElement} */ (this.#requireRef('form'));
  }

  /** @returns {HTMLElement} */
  get countryListElement() {
    return /** @type {HTMLElement} */ (this.#requireRef('countryList'));
  }

  /** @returns {HTMLScriptElement} */
  get dataElement() {
    return /** @type {HTMLScriptElement} */ (this.#requireRef('data'));
  }

  /**
   * @template {Element | Element[]} T
   * @param {string} ref
   * @returns {T}
   */
  #requireRef(ref) {
    const element = this.refs?.[ref];
    if (!element) {
      throw new Error(`Missing ref "${ref}" in localization-dialog`);
    }
    return /** @type {T} */ (element);
  }
}

customElements.define('localization-dialog', LocalizationDialog);
